import type { CustomEmojiListing, EmojiUpload } from "@beacon/core/custom-emoji"
import { queryOptions, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import { call } from "@/administration/client.ts"
import { Refused } from "@/administration/refused.ts"
import { uploadable } from "@/photos/uploadable.ts"
import { customCatalogOf, type CustomCatalog } from "./custom-catalog.ts"

export type { CustomCatalog } from "./custom-catalog.ts"

/*
 * The catalogue, in the web app's one query cache (ADR-0015), so signing out drops it with
 * everything else: read once a page first wants it, read again when a page wants it after five
 * minutes, or when a reaction holds an emoji it doesn't (one added since, by someone else), at most
 * once a minute. What this browser changes it keeps at once.
 */

const freshFor = 5 * 60_000
const missesAtMostEvery = 60_000

/** The catalogue as the web API lists it, every emoji ever added, removed ones too. */
export const customEmojiQuery = queryOptions({
  queryKey: ["custom-emoji"],
  queryFn: async () => (await call<{ emoji: CustomEmojiListing[] }>("GET", "/custom-emoji")).emoji,
  staleTime: freshFor,
  refetchOnWindowFocus: false,
})

/** Keeps an emoji this browser added, renamed or removed, as the web API answered it. */
export function keepCustomEmoji(queryClient: QueryClient, listing: CustomEmojiListing) {
  queryClient.setQueryData(customEmojiQuery.queryKey, (listings = []) => [
    ...listings.filter(({ id }) => id !== listing.id),
    listing,
  ])
}

let missedAt = 0

/**
 * Says the catalogue lacks an emoji a reaction holds, which reads it again, at most once a minute.
 */
export function useMissingCustomEmoji() {
  const queryClient = useQueryClient()
  return useCallback(() => {
    if (Date.now() - missedAt < missesAtMostEvery) return
    missedAt = Date.now()
    void queryClient.invalidateQueries({ queryKey: customEmojiQuery.queryKey })
  }, [queryClient])
}

/**
 * The catalogue, or none until it has been read (or while it can't be). `wanted` starts reading it;
 * a page that only might need it passes whether it does.
 */
export function useCustomEmoji(wanted = true): CustomCatalog | undefined {
  return useQuery({ ...customEmojiQuery, enabled: wanted, select: customCatalogOf }).data
}

/**
 * The catalogue as a page that lists it reads it: the catalogue once read, whether reading it
 * failed, and a way to try again.
 */
export function useCustomEmojiState() {
  const read = useQuery({ ...customEmojiQuery, select: customCatalogOf })
  return {
    catalog: read.data,
    failed: read.isError && !read.data,
    retry: () => void read.refetch(),
  }
}

/**
 * Adds a picture to the catalogue as `name`, kept here at once: made ready to upload (`uploadable`:
 * a HEIC photo as a JPEG, a JPEG without where it was taken), put straight into the store through
 * the URL the web API signs, then added, which is when the server draws it. Throws `Refused` with
 * the web API's refusal, or `upload-failed` when the store refused the bytes.
 */
export async function addCustomEmoji(queryClient: QueryClient, file: File, name: string) {
  const picture = await uploadable(file).catch(() => {
    throw new Refused("unreadable", {})
  })
  const upload = await call<EmojiUpload>("POST", "/custom-emoji/uploads", {
    contentType: picture.type,
    size: picture.size,
  })
  const put = await fetch(upload.url, { method: "PUT", headers: upload.headers, body: picture })
  if (!put.ok) throw new Refused("upload-failed", {})
  const added = await call<CustomEmojiListing>("POST", "/custom-emoji", {
    uploadId: upload.uploadId,
    name,
  })
  keepCustomEmoji(queryClient, added)
  return added
}

/** Renames an emoji, as an Admin, kept here at once. */
export async function renameCustomEmoji(queryClient: QueryClient, emojiId: string, name: string) {
  const renamed = await call<CustomEmojiListing>(
    "PATCH",
    `/custom-emoji/${encodeURIComponent(emojiId)}`,
    { name },
  )
  keepCustomEmoji(queryClient, renamed)
  return renamed
}

/** Removes an emoji, as an Admin, kept here at once. */
export async function removeCustomEmoji(queryClient: QueryClient, emojiId: string) {
  const removed = await call<CustomEmojiListing>(
    "DELETE",
    `/custom-emoji/${encodeURIComponent(emojiId)}`,
  )
  keepCustomEmoji(queryClient, removed)
  return removed
}
